import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Play, RefreshCw, MapPin, Gauge, Clock, Car } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

// Fix Leaflet default-marker paths (bundled CDN alt)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom gold pin
const goldPin = new L.DivIcon({
  className: "kto-pin",
  html: `<div style="width:24px;height:34px;position:relative;transform:translate(-12px,-32px);">
    <div style="width:24px;height:24px;background:#D4AF37;border:2px solid #050505;border-radius:50%;box-shadow:0 4px 14px rgba(0,0,0,.6);"></div>
    <div style="position:absolute;left:9px;top:20px;width:0;height:0;border-left:3px solid transparent;border-right:3px solid transparent;border-top:8px solid #D4AF37;"></div>
  </div>`,
});

const AU_CENTER = [-25.2744, 133.7751];

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
    } else {
      const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
    }
  }, [points, map]);
  return null;
}

export default function AdminFleetMap() {
  const [locations, setLocations] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMock, setShowMock] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [lRes, gRes, eRes] = await Promise.all([
        api.get("/admin/telematics/fleet-locations"),
        api.get("/admin/telematics/geofences"),
        api.get("/admin/telematics/events", { params: { limit: 20 } }),
      ]);
      setLocations(lRes.data);
      setGeofences(gRes.data);
      setEvents(eRes.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 15000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  useEffect(() => {
    if (!selectedId) { setHistory([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/admin/telematics/vehicles/${selectedId}/locations`, { params: { limit: 100 } });
        setHistory(data);
      } catch { /* ignore */ }
    })();
  }, [selectedId]);

  const ackEvent = async (id) => {
    try {
      await api.post(`/admin/telematics/events/${id}/acknowledge`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const points = locations.map((v) => ({ lat: v.last_lat, lng: v.last_lng }));

  return (
    <div data-testid="admin-fleet-map-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/admin/geofences" data-testid="admin-fleetmap-geofences-link" className="text-xs text-[#D4AF37] hover:text-[#F6ECCC] tracking-wide">Geofences →</Link>
            <button onClick={load} data-testid="admin-fleetmap-refresh" className="text-white/60 hover:text-[#D4AF37]"><RefreshCw size={14} /></button>
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]">
              <ArrowLeft size={14} /> Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-10">
        <div className="label-eyebrow mb-3">FLEET · Live GPS</div>
        <h1 className="font-display text-4xl md:text-5xl mb-6" style={{ fontWeight: 300 }}>
          Fleet <span className="italic text-[#D4AF37]">Map</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
          <Kpi label="TRACKED VEHICLES" value={locations.length} />
          <Kpi label="ACTIVE GEOFENCES" value={geofences.filter((g) => g.active).length} />
          <Kpi label="RECENT EVENTS" value={events.length} />
          <Kpi label="UNACKNOWLEDGED" value={events.filter((e) => !e.acknowledged).length}
               tone={events.filter((e) => !e.acknowledged).length > 0 ? "warn" : undefined} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 rounded-lg overflow-hidden border border-white/10 bg-[#0a0a0a]">
            <div className="h-[540px] w-full">
              {loading ? (
                <div className="h-full flex items-center justify-center text-white/40 text-sm">Loading map…</div>
              ) : (
                <MapContainer
                  center={AU_CENTER}
                  zoom={4}
                  style={{ height: "100%", width: "100%" }}
                  data-testid="admin-fleet-map-container"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <AutoFit points={points} />

                  {/* Geofence polygons */}
                  {geofences.filter((g) => g.active && g.polygon?.length >= 3).map((g) => (
                    <Polygon
                      key={g.id}
                      positions={g.polygon}
                      pathOptions={{
                        color: g.type === "restricted" ? "#ef4444" : "#22c55e",
                        weight: 2, fillOpacity: 0.12,
                      }}
                    >
                      <Popup>
                        <div style={{ color: "#050505", fontFamily: "sans-serif" }}>
                          <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#666" }}>
                            {g.type} zone
                          </div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{g.name}</div>
                          <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                            Alert on: {g.alert_on}
                          </div>
                        </div>
                      </Popup>
                    </Polygon>
                  ))}

                  {/* Selected vehicle history */}
                  {selectedId && history.length > 1 && (
                    <Polyline
                      positions={history.map((h) => [h.lat, h.lng])}
                      pathOptions={{ color: "#D4AF37", weight: 3, opacity: 0.7, dashArray: "4 6" }}
                    />
                  )}

                  {/* Vehicle pins */}
                  {locations.map((v) => (
                    <Marker
                      key={v.id}
                      position={[v.last_lat, v.last_lng]}
                      icon={goldPin}
                      eventHandlers={{ click: () => setSelectedId(v.id) }}
                    >
                      <Popup>
                        <div style={{ color: "#050505", fontFamily: "sans-serif", minWidth: 180 }}>
                          <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "#666" }}>
                            {v.rego}
                          </div>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                            {v.year} {v.make} {v.model}
                          </div>
                          <div style={{ fontSize: 12 }}>
                            {v.last_speed != null ? `${Math.round(v.last_speed)} km/h · ` : ""}
                            {v.last_seen_at?.slice(11, 16)}
                          </div>
                          <a
                            href={`https://www.google.com/maps?q=&layer=c&cbll=${v.last_lat},${v.last_lng}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ display: "inline-block", marginTop: 8, color: "#D4AF37", fontSize: 11, textDecoration: "underline" }}
                          >
                            View Street View →
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="border border-white/10 rounded-lg bg-[#0a0a0a] p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/70 text-xs tracking-widest">TRACKED VEHICLES</div>
                <button
                  onClick={() => setShowMock(true)}
                  data-testid="admin-fleetmap-mock-drive"
                  className="text-[10px] tracking-widest text-[#D4AF37] hover:text-[#F6ECCC] inline-flex items-center gap-1"
                ><Play size={10} /> SIMULATE</button>
              </div>
              {locations.length === 0 ? (
                <div className="text-xs text-white/40 py-3">
                  No location data yet. Use "SIMULATE" to test with mock GPS,
                  or push locations via <code className="text-[#D4AF37]">/api/portal/location</code>.
                </div>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {locations.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedId(v.id)}
                      data-testid={`admin-fleetmap-vehicle-${v.id}`}
                      className={`w-full text-left px-3 py-2 rounded text-xs border ${
                        selectedId === v.id
                          ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37]"
                          : "border-transparent text-white/70 hover:bg-white/5"
                      }`}
                    >
                      <div className="font-medium">{v.year} {v.make} {v.model}</div>
                      <div className="text-white/50 flex items-center gap-2 mt-0.5">
                        <MapPin size={10} /> {v.rego}
                        {v.last_speed != null && <><Gauge size={10} className="ml-1" /> {Math.round(v.last_speed)}</>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Alerts */}
            <div className="border border-white/10 rounded-lg bg-[#0a0a0a] p-4">
              <div className="text-white/70 text-xs tracking-widest mb-2">RECENT GEOFENCE EVENTS</div>
              {events.length === 0 ? (
                <div className="text-xs text-white/40 py-2">No events yet.</div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {events.map((e) => (
                    <div key={e.id} className="text-xs border-l-2 border-[#D4AF37]/40 pl-3 py-1">
                      <div className="text-white">
                        <span className={e.event === "enter" ? "text-red-300" : "text-emerald-300"}>
                          {e.event === "enter" ? "ENTER" : "EXIT"}
                        </span>
                        {" "}<span className="text-white/60">{e.geofence_name}</span>
                      </div>
                      <div className="text-white/50">{e.vehicle_label} · {e.vehicle_rego}</div>
                      <div className="flex items-center justify-between text-white/40 mt-0.5">
                        <span><Clock size={10} className="inline mr-1" />{e.created_at?.slice(11, 16)}</span>
                        {!e.acknowledged && (
                          <button
                            onClick={() => ackEvent(e.id)}
                            data-testid={`admin-fleetmap-ack-${e.id}`}
                            className="text-[10px] text-[#D4AF37] hover:text-[#F6ECCC] tracking-widest"
                          >ACK</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {showMock && <MockDriveModal locations={locations} onClose={() => setShowMock(false)} onDone={() => { setShowMock(false); load(); }} />}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return (
    <div className="border border-white/10 rounded-lg p-4 bg-[#0a0a0a]">
      <div className="text-[10px] tracking-widest text-white/50 mb-1">{label}</div>
      <div className={`font-display text-2xl ${tone === "warn" ? "text-red-300" : "text-white"}`} style={{ fontWeight: 300 }}>{value}</div>
    </div>
  );
}

function MockDriveModal({ locations, onClose, onDone }) {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: "", lat: -33.865143, lng: 151.209900,   // Sydney default
    heading: 45, speed_kmh: 40, steps: 8, step_seconds: 1,
  });
  const [running, setRunning] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    api.get("/admin/rto/fleet").then((r) => setVehicles(r.data)).catch(() => {});
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id) return toast.error("Select a vehicle");
    setRunning(true);
    try {
      const { data } = await api.post("/admin/telematics/mock-drive", {
        ...form,
        lat: Number(form.lat), lng: Number(form.lng),
        heading: Number(form.heading), speed_kmh: Number(form.speed_kmh),
        steps: Number(form.steps), step_seconds: Number(form.step_seconds),
      });
      toast.success(`Emitted ${data.points} location points`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setRunning(false); }
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-4"
    >
      <div data-testid="admin-fleetmap-mock-modal" className="bg-[#0a0a0a] border border-white/10 rounded-lg w-full max-w-md p-6">
        <div className="text-[#D4AF37] text-[10px] tracking-widest">DEV · MOCK GPS FEED</div>
        <div className="font-display text-xl mb-4">Simulate a drive</div>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <label className="block">
            <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">VEHICLE</span>
            <select
              data-testid="admin-fleetmap-mock-vehicle"
              required value={form.vehicle_id}
              onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}
              className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2"
            >
              <option value="">— Select —</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.rego} · {v.make} {v.model}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <NumField label="START LAT" value={form.lat} onChange={(v) => setForm({ ...form, lat: v })} step="0.0001" testid="admin-fleetmap-mock-lat" />
            <NumField label="START LNG" value={form.lng} onChange={(v) => setForm({ ...form, lng: v })} step="0.0001" testid="admin-fleetmap-mock-lng" />
            <NumField label="HEADING (°)" value={form.heading} onChange={(v) => setForm({ ...form, heading: v })} />
            <NumField label="SPEED (km/h)" value={form.speed_kmh} onChange={(v) => setForm({ ...form, speed_kmh: v })} />
            <NumField label="STEPS" value={form.steps} onChange={(v) => setForm({ ...form, steps: v })} />
            <NumField label="STEP SEC" value={form.step_seconds} onChange={(v) => setForm({ ...form, step_seconds: v })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest text-white/70 hover:text-white">CANCEL</button>
            <button
              type="submit"
              data-testid="admin-fleetmap-mock-submit"
              disabled={running}
              className="bg-[#D4AF37] text-[#050505] font-semibold rounded px-5 py-2 text-xs tracking-widest hover:bg-[#E5C158] disabled:opacity-50"
            >{running ? "RUNNING…" : "RUN SIMULATION"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, step = "1", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-widest text-white/50 mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type="number" step={step} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#050505] border border-white/10 rounded px-3 py-2 text-sm"
      />
    </label>
  );
}
